
import React from 'react';
import { MealSession, SymptomSession, Language, VisualDensity } from '../types';
import { t } from '../locales';

interface WeeklyCalendarProps {
  sessions: (MealSession | SymptomSession)[];
  language: Language;
  density?: VisualDensity;
  type?: 'meal' | 'symptom';
  currentDate?: Date; // New prop to control the focused date
  onDayClick?: (date: Date) => void;
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({ sessions, language, density = 'standard', type = 'meal', currentDate = new Date(), onDayClick }) => {
  // Helper to check if a date has data
  const hasData = (date: Date) => {
    const dateString = date.toDateString();
    return sessions.some(s => new Date(s.timestamp).toDateString() === dateString);
  };

  const today = new Date(); // Real today for "TODAY" highlight if needed, or just strict selection logic
  
  // Calculate start of the week based on currentDate
  const selectedDay = currentDate.getDay(); // 0 is Sunday
  const diff = currentDate.getDate() - selectedDay + (selectedDay === 0 ? -6 : 1); // Adjust to Monday start
  
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(diff); 

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    weekDays.push(d);
  }

  // Density Classes - Thinner versions
  const containerPadding = density === 'compact' ? 'py-1 mb-1' : density === 'comfort' ? 'py-3 mb-2' : 'py-2 mb-1';
  const dayNameClass = density === 'compact' ? 'text-[8px]' : 'text-[9px]';
  const circleSize = density === 'compact' ? 'w-5 h-5 text-[9px]' : density === 'comfort' ? 'w-8 h-8 text-xs' : 'w-6 h-6 text-[10px]';
  const gapSize = density === 'compact' ? 'gap-0.5' : 'gap-1';
  const dotSize = density === 'compact' ? 'w-0.5 h-0.5' : 'w-1 h-1';

  // Theme Colors
  const isSymptom = type === 'symptom';
  const textHighlight = isSymptom ? 'text-orange-500 dark:text-orange-400' : 'text-emerald-600 dark:text-emerald-400';
  const bgHighlight = isSymptom ? 'bg-orange-500' : 'bg-emerald-600';
  const dotActive = isSymptom ? 'bg-orange-500' : 'bg-emerald-500';
  const dotActiveToday = isSymptom ? 'bg-orange-600 dark:bg-orange-400' : 'bg-emerald-600 dark:bg-emerald-400';

  return (
    <div id="tour-weekly-calendar" className={`bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 ${containerPadding} ${density === 'compact' ? 'rounded-md' : 'rounded-xl'}`}>
      <div className="flex justify-between items-end">
        {weekDays.map((date, index) => {
          // Highlight based on the currently selected date passed from parent
          const isSelected = date.toDateString() === currentDate.toDateString();
          const active = hasData(date);
          const dayName = date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'narrow' });
          const dayNum = date.getDate();
          
          // Vertical bar logic
          const borderClass = index < 6 ? 'border-r border-gray-100 dark:border-gray-700' : '';

          return (
            <button 
              key={index} 
              onClick={() => onDayClick && onDayClick(date)}
              className={`flex flex-col items-center justify-end flex-1 ${gapSize} ${borderClass} pb-1 active:scale-90 transition-transform cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg`}
            >
              <span className={`${dayNameClass} uppercase ${isSelected ? `${textHighlight} font-bold` : 'text-gray-400'}`}>
                {dayName}
              </span>
              
              <div 
                className={`
                  ${circleSize} rounded-full flex items-center justify-center font-medium transition-all
                  ${isSelected 
                    ? `${bgHighlight} text-white shadow-md` 
                    : 'text-gray-700 dark:text-gray-300'
                  }
                `}
              >
                {dayNum}
              </div>

              {/* Dot Indicator */}
              <div className={`h-1 flex items-center justify-center`}>
                 <div className={`${dotSize} rounded-full ${active ? (isSelected ? dotActiveToday : dotActive) : 'bg-transparent'}`}></div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyCalendar;
